import type { ChatLocationPayload } from "../types";

type ActiveChatLiveLocationShare = {
  id: string;
  expiresAt?: string;
  isActive?: boolean;
};

export type ChatLiveLocationState = {
  title: string;
  badgeLabel: string;
  detailLabel: string | null;
  mapsLabel: string;
  isExpired: boolean;
};

const formatLiveLocationRemaining = (expiresAt?: string, now = Date.now()) => {
  if (!expiresAt) return null;
  const remainingMs = new Date(expiresAt).getTime() - now;
  if (!Number.isFinite(remainingMs) || remainingMs <= 0) return "Ended";
  const minutes = Math.ceil(remainingMs / 60000);
  if (minutes >= 60) {
    const hours = Math.floor(minutes / 60);
    const leftoverMinutes = minutes % 60;
    return leftoverMinutes > 0 ? `${hours}h ${leftoverMinutes}m left` : `${hours}h left`;
  }
  return `${minutes}m left`;
};

export const getChatLiveLocationState = (
  location: ChatLocationPayload,
  activeShare?: ActiveChatLiveLocationShare | null,
  now = Date.now(),
): ChatLiveLocationState => {
  if (location.kind !== "live") {
    return {
      title: location.label || "Current location",
      badgeLabel: "",
      detailLabel: null,
      mapsLabel: "Open in Maps",
      isExpired: false,
    };
  }

  const effectiveExpiresAt = activeShare?.expiresAt || location.expiresAt;
  const activeShareExpiresAtMs = activeShare?.expiresAt
    ? new Date(activeShare.expiresAt).getTime()
    : Number.NaN;
  const hasActiveShare = Boolean(
    activeShare &&
      activeShare.isActive !== false &&
      (!activeShare.expiresAt ||
        !Number.isFinite(activeShareExpiresAtMs) ||
        activeShareExpiresAtMs > now),
  );
  const remainingLabel = formatLiveLocationRemaining(effectiveExpiresAt, now);

  if (hasActiveShare || (!location.liveShareId && remainingLabel && remainingLabel !== "Ended")) {
    return {
      title: "Live location",
      badgeLabel: remainingLabel || "Live",
      detailLabel: null,
      mapsLabel: "Open in Maps",
      isExpired: false,
    };
  }

  return {
    title: "Expired live location",
    badgeLabel: "Expired",
    detailLabel: "No longer valid.",
    mapsLabel: "Open last location in Maps",
    isExpired: true,
  };
};